import { useToggleLoginsVisibility } from "@/api/mutations/logins";
import { useMe } from "@/api/queries/auth";
import { useGetLogins } from "@/api/queries/logins";
import type { LoginItemType } from "@/api/queries/logins/types";
import { CountryItem } from "@/components/country";
import { CountrySelect } from "@/components/country-select";
import { TablePagination } from "@/components/table/table-pagination";
import { type SortDirection, TableSort } from "@/components/table/table-sort";
import { Button } from "@/components/ui/button";
import { DatePickerWithRange } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  type TableColumn,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { UserSelect } from "@/components/user-select";
import { ROLES } from "@/constants/users";
import { formatDate } from "@/lib/date";
import { LoginStatus } from "@/pages/dashboard/components/cells/login-status";
import { DebugDialog } from "@/pages/dashboard/components/debug-dialog";
import { EditLoginDialog } from "@/pages/dashboard/components/edit-login-dialog";
import { ExportDialog } from "@/pages/dashboard/components/export-dialog";
import { useLoginsStore } from "@/pages/dashboard/hooks/use-logins-state";
import { EllipsisVertical, Eye, EyeOff, Save, X } from "lucide-react";
import { Fragment, useMemo, useState } from "react";

export const LoginsTable = () => {
  const { data: me } = useMe();
  const { state, setState } = useLoginsStore();
  const { data, isLoading } = useGetLogins(state);
  const { mutateAsync: toggleLoginsVisibility, isPending: loginVisibilityIsUpdating } =
    useToggleLoginsVisibility();

  const [isHideMode, setIsHideMode] = useState(false);
  const [visibilityChanges, setVisibilityChanges] = useState<Record<number, boolean>>({});

  const isAdmin = me?.data.role === ROLES.ADMIN;

  const handleEnterHideMode = () => {
    setVisibilityChanges({});
    setIsHideMode(true);
  };

  const handleCancelHideMode = () => {
    setIsHideMode(false);
    setVisibilityChanges({});
  };

  const handleToggleSingleVisibility = (id: number) => {
    const loginItem = (data?.data ?? []).find((item) => item.id === id);
    setVisibilityChanges((prev) => ({
      ...prev,
      [id]: !(prev[id] ?? !loginItem?.hidden),
    }));
  };

  const handleSaveVisibility = async () => {
    const payload = Object.entries(visibilityChanges).map(([id, visible]) => ({
      id: Number(id),
      visible,
    }));

    try {
      await toggleLoginsVisibility({ logins: payload });
      setIsHideMode(false);
      setVisibilityChanges({});
    } catch (error) {
      console.error("Failed to update logins visibility:", error);
    }
  };

  const columns = useMemo<TableColumn<LoginItemType>[]>(() => {
    const result = [
      isHideMode && {
        key: "visibility",
        label: "Visibility",
        render: (login: LoginItemType) => {
          const isVisible = visibilityChanges[login.id] ?? !login.hidden;
          return (
            <TableCell>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleToggleSingleVisibility(login.id)}
                title={isVisible ? "Hide Login" : "Show Login"}
              >
                {isVisible ? (
                  <Eye className="size-4 text-green-600" />
                ) : (
                  <EyeOff className="size-4 text-muted-foreground opacity-50" />
                )}
                <span className="sr-only">Toggle visibility</span>
              </Button>
            </TableCell>
          );
        },
      },
      {
        key: "username",
        className: "min-w-[180px] md:min-w-unset",
        label: (
          <Input
            placeholder="Username"
            value={state.username}
            onChange={(e) => setState({ username: e.target.value })}
          />
        ),
        sortable: true,
        render: (login: LoginItemType) => (
          <TableCell className="font-medium">{login.username}</TableCell>
        ),
      },
      me?.data?.role === ROLES.ADMIN && {
        key: "owner",
        label: (
          <UserSelect
            userRole={ROLES.AGENT}
            value={state.owner}
            onValueChange={(value) => setState({ owner: value })}
          />
        ),
        render: (login: LoginItemType) => (
          <TableCell className="font-medium">{login.owner.username}</TableCell>
        ),
      },
      {
        key: "location",
        label: (
          <CountrySelect
            placeholder="Select Location"
            empyPlaceholder="No locations found"
            searchPlaceholder="Search Location"
            value={state.location}
            onSelect={(location) => setState({ location })}
          />
        ),
        sortable: true,
        render: (login: LoginItemType) => (
          <TableCell>
            <CountryItem country={login.location} city={login.city} />
          </TableCell>
        ),
      },
      {
        key: "status",
        label: (
          <Select
            value={state.active ? "active" : state.active === undefined ? null : "inactive"}
            onValueChange={(status) =>
              setState({
                active: status ? status === "active" : undefined,
              })
            }
          >
            <SelectGroup>
              <SelectTrigger>
                {state.active ? "Active" : state.active === undefined ? "Status" : "Inactive"}
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={null}>Select Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </SelectGroup>
          </Select>
        ),
        sortable: true,
        render: (login: LoginItemType) => <LoginStatus loginItem={login} />,
      },
      {
        key: "createdAt",
        label: "Created",
        sortable: true,
        render: (login: LoginItemType) => (
          <TableCell>{formatDate(new Date(login.createdAt))}</TableCell>
        ),
      },
      {
        key: "export",
        label: "Export",
        render: (loginItem: LoginItemType) => (
          <TableCell>
            <ExportDialog loginItem={loginItem} />
          </TableCell>
        ),
      },
      {
        key: "actions",
        label: "Actions",
        className: "text-right",
        render: (loginItem: LoginItemType) => (
          <TableCell className="text-right">
            <Popover>
              <PopoverTrigger>
                <Button variant="ghost" size="icon">
                  <EllipsisVertical className="size-4" />
                  <span className="sr-only">Open actions</span>
                </Button>
              </PopoverTrigger>

              <PopoverContent align="end" className="w-36 p-2">
                <div className="flex flex-col gap-1">
                  <EditLoginDialog loginItem={loginItem} />
                  {isAdmin && <DebugDialog loginItem={loginItem} />}
                </div>
              </PopoverContent>
            </Popover>
          </TableCell>
        ),
      },
    ];

    return result.filter(Boolean) as TableColumn<LoginItemType>[];
  }, [state, data?.data, isAdmin, isHideMode, visibilityChanges]);

  const handleSortChange = (key: string) => {
    return (sortDirection: "asc" | "desc" | undefined) => {
      setState({ sortBy: key, orderBy: sortDirection });
    };
  };

  return (
    <>
      <div className="flex flex-wrap gap-4 mb-8 justify-between items-center">
        <div className="min-w-[180px]">
          <DatePickerWithRange
            date={{
              from: state.minCreatedAt ? new Date(state.minCreatedAt) : undefined,
              to: state.maxCreatedAt ? new Date(state.maxCreatedAt) : undefined,
            }}
            onSelect={(range) =>
              setState({
                minCreatedAt: range?.from?.toISOString(),
                maxCreatedAt: range?.to?.toISOString(),
              })
            }
            label="Created"
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant={state.showHidden ? "secondary" : "outline"}
            onClick={() => {
              setState({ showHidden: !state.showHidden });
            }}
            disabled={isHideMode || loginVisibilityIsUpdating}
          >
            {state.showHidden ? (
              <>
                <Eye className="size-4 mr-2" />
                Showing Hidden
              </>
            ) : (
              <>
                <EyeOff className="size-4 mr-2" />
                Show Hidden
              </>
            )}
          </Button>

          {!isHideMode ? (
            <Button variant="outline" onClick={handleEnterHideMode}>
              Hide Control
            </Button>
          ) : (
            <>
              <Button
                variant="default"
                onClick={handleSaveVisibility}
                disabled={loginVisibilityIsUpdating}
              >
                <Save className="size-4 mr-2" />
                Save Visibility
              </Button>
              <Button
                variant="ghost"
                onClick={handleCancelHideMode}
                disabled={loginVisibilityIsUpdating}
              >
                <X className="size-4 mr-2" />
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="min-w-0 overflow-hidden flex">
        <div className="rounded-xl border overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((col) => (
                  <TableHead key={col.key} className={col.className}>
                    <div className="flex items-center gap-1">
                      {col.label}{" "}
                      {col.sortable && (
                        <TableSort
                          onChange={handleSortChange(col.key)}
                          direction={
                            col.key === state.sortBy ? (state.orderBy as SortDirection) : undefined
                          }
                        />
                      )}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <div className="flex items-center justify-center p-8">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && (data?.data || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <div className="flex items-center justify-center p-8 text-muted-foreground">
                      No logins found
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {(data?.data || []).map((login) => (
                <TableRow key={login.id}>
                  {columns.map((col) => (
                    <Fragment key={col.key}>{col.render(login)}</Fragment>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex justify-end">
            <TablePagination
              page={state.page ?? 1}
              pageSize={state.pageSize ?? 20}
              onPageSizeChange={(pageSize) => setState({ pageSize })}
              onPageChange={(page) => setState({ page })}
              total={data?.pagination.total ?? 1}
            />
          </div>
        </div>
      </div>
    </>
  );
};
